/**
 * Hanging up in a VRI session (`vri-` rooms; Robert, 2026-10-09:
 * "if the vri client hang up, the call ends").
 *
 * - The client who started the session (the server's calls.client_id, the
 *   stored call's clientId) ends it for everyone: "End call".
 * - Everyone else only leaves: the interpreter, the guests who joined by a
 *   link and any other account in the room: "Leave call". The server ends a
 *   session of two people (the client and the interpreter) when the
 *   interpreter leaves (vrs-server/ws/vri-session-hangup.js).
 *
 * Jitsi moderator status is never used: the client and the interpreter both
 * hold a moderator token. No storage-layer imports here, as in
 * groupEndForAll.ts.
 */
import { LEAVE_CALL_LABEL } from './groupEndForAll';
/** The VRI client's hang-up: the session ends for everyone. */
export const END_CALL_LABEL = 'End call';
/**
 * Whether a room is a VRI session's room (the server's `vri-` rooms).
 *
 * @param {string} [roomName] - The conference room.
 * @returns {boolean}
 */
export function isVriRoomName(roomName) {
    return typeof roomName === 'string' && /^vri-/i.test(roomName);
}
/**
 * Whether this person started the VRI session in this room: a signed-in
 * client whose stored call (vrs_active_call) is this room's call and names
 * them as its client. A guest's room token (role 'guest') never counts, also
 * in a browser where a client account is signed in. The stored call is
 * shared by every tab and can outlive its call, so a call stored for another
 * room never counts.
 *
 * @param {IStoredVriCall|null} call - The stored active call.
 * @param {string|undefined} userId - The signed-in account.
 * @param {string|null} role - The signed-in role.
 * @param {string|undefined} currentRoom - The room this page or app is in.
 * @param {string|undefined} roomTokenRole - The room token's role (features/base/jwt).
 * @returns {boolean}
 */
export function isVriSessionStarter(call, userId, role, currentRoom, roomTokenRole) {
    return role === 'client'
        && roomTokenRole !== 'guest' && roomTokenRole !== 'observer'
        && typeof userId === 'string' && userId.length > 0
        && Boolean(call) && call?.clientId === userId
        && typeof call?.callId === 'string' && call.callId.length > 0
        && isVriRoomName(call?.roomName)
        && typeof currentRoom === 'string' && currentRoom.toLowerCase() === String(call?.roomName).toLowerCase();
}
/**
 * The words on the VRI hang-up: "End call" for the client who started the
 * session, "Leave call" for everyone else.
 *
 * @param {boolean} starter - This person started the session.
 * @returns {string}
 */
export function vriHangupLabel(starter) {
    return starter ? END_CALL_LABEL : LEAVE_CALL_LABEL;
}
